import { useEffect, useMemo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  type DigitalTwinListItem,
  contrastText,
  digitalTwinShortLabel,
} from "../../lib/digitalTwinsApi";

const FALLBACK_COLOR = "#6366f1";

/**
 * 2D satellite map of a site's georeferenced digital twins. Each twin with a
 * latitude/longitude is drawn as a readable name-tag; clicking a tag opens the
 * twin's viewer. Hovering a tag (or, from the parent, a list row) cross-
 * highlights via the shared `hoveredId`.
 *
 * Basemap is Esri World Imagery raster tiles — free, no API key/token.
 */

interface DigitalTwinsMapProps {
  twins: DigitalTwinListItem[];
  /** Twin id → colour, shared with the list so map/list legends agree. */
  colorById: Map<string, string>;
  hoveredId: string | null;
  onHover: (id: string | null) => void;
  onOpen: (id: string) => void;
}

// Esri World Imagery — public satellite raster tiles, no key required.
const ESRI_IMAGERY_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTRIBUTION =
  "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

// Injected once with the map. Styles the custom divIcon name-tags. Kept here
// (rather than a Tailwind class string) because Leaflet renders the tag HTML
// outside React into its own map pane, so JIT-purged utility classes wouldn't
// survive — plain CSS on stable class names does.
const TAG_STYLES = `
/* Two-class specificity so this beats Leaflet's default .leaflet-div-icon
   (white fill + grey border) regardless of stylesheet insertion order. */
.leaflet-div-icon.dt-tag-icon { background: transparent; border: 0; }
.dt-tag-anchor { position: absolute; left: 0; top: 0; }
/* The tag's fill/text colour come from an inline style (the twin's assigned
   colour); this rule owns only layout + the hover/active affordance. */
.dt-tag {
  position: absolute; bottom: 9px; left: 0; transform: translateX(-50%);
  white-space: nowrap; padding: 2px 7px; border-radius: 7px;
  font-size: 11px; line-height: 1.2; font-weight: 700; letter-spacing: 0.02em;
  border: 1px solid rgba(0, 0, 0, 0.35); box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  cursor: pointer; transition: transform 120ms ease, box-shadow 120ms ease;
}
.dt-tag--active {
  transform: translateX(-50%) scale(1.12);
  box-shadow: 0 0 0 2px #fff, 0 3px 10px rgba(0, 0, 0, 0.6);
}
.dt-tag-dot {
  position: absolute; left: -5px; top: -5px; width: 10px; height: 10px; border-radius: 9999px;
  border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}
`;

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}

function makeTagIcon(name: string, color: string, active: boolean): L.DivIcon {
  const tagClass = active ? "dt-tag dt-tag--active" : "dt-tag";
  const fg = contrastText(color);
  const label = escapeHtml(digitalTwinShortLabel(name));
  return L.divIcon({
    className: "dt-tag-icon",
    // 0-size icon anchored exactly at the coordinate; the dot marks the point
    // and the colour-coded short-code pill floats above it via CSS.
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html:
      `<div class="dt-tag-anchor">` +
      `<div class="${tagClass}" style="background:${color};color:${fg}">${label}</div>` +
      `<div class="dt-tag-dot" style="background:${color}"></div>` +
      `</div>`,
  });
}

function hasCoords(
  t: DigitalTwinListItem,
): t is DigitalTwinListItem & { latitude: number; longitude: number } {
  return t.latitude != null && t.longitude != null;
}

/** Imperatively fit the map to all markers whenever the point set changes. */
function FitBounds({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    // The map lives in a flex column that may size after mount; make sure
    // Leaflet has the right pixel dimensions before fitting.
    map.invalidateSize();
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 16);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 17 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

function TwinMarker({
  twin,
  color,
  active,
  onHover,
  onOpen,
}: {
  twin: DigitalTwinListItem & { latitude: number; longitude: number };
  color: string;
  active: boolean;
  onHover: (id: string | null) => void;
  onOpen: (id: string) => void;
}) {
  const icon = useMemo(
    () => makeTagIcon(twin.name, color, active),
    [twin.name, color, active],
  );
  return (
    <Marker
      position={[twin.latitude, twin.longitude]}
      icon={icon}
      zIndexOffset={active ? 1000 : 0}
      eventHandlers={{
        click: () => onOpen(twin.id),
        mouseover: () => onHover(twin.id),
        mouseout: () => onHover(null),
      }}
    />
  );
}

export function DigitalTwinsMap({
  twins,
  colorById,
  hoveredId,
  onHover,
  onOpen,
}: DigitalTwinsMapProps) {
  const geoTwins = useMemo(() => twins.filter(hasCoords), [twins]);
  const points = useMemo<Array<[number, number]>>(
    () => geoTwins.map((t) => [t.latitude, t.longitude]),
    [geoTwins],
  );

  // `isolate` on the wrapper confines Leaflet's internal z-indexes (panes and
  // controls go up to 1000) to their own stacking context, so they can't paint
  // over modals/overlays elsewhere on the page (e.g. the twin Details modal).
  return (
    <div className="relative isolate h-full w-full overflow-hidden rounded-xl border border-surface-700/50">
      <style>{TAG_STYLES}</style>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        scrollWheelZoom
        worldCopyJump
        className="h-full w-full"
        style={{ background: "#0b0e14" }}
      >
        <TileLayer url={ESRI_IMAGERY_URL} attribution={ESRI_ATTRIBUTION} maxZoom={19} />
        <FitBounds points={points} />
        {geoTwins.map((t) => (
          <TwinMarker
            key={t.id}
            twin={t}
            color={colorById.get(t.id) ?? FALLBACK_COLOR}
            active={hoveredId === t.id}
            onHover={onHover}
            onOpen={onOpen}
          />
        ))}
      </MapContainer>

      {geoTwins.length === 0 && (
        <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
          <div className="pointer-events-auto rounded-lg border border-surface-700/60 bg-surface-900/85 px-4 py-3 text-center text-sm text-surface-300 shadow-lg">
            No georeferenced digital twins yet.
            <br />
            <span className="text-surface-500">
              Add latitude/longitude when uploading, or set it from a twin&apos;s Details.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
